import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import type { TFunction } from 'i18next';
import { selectSiteId } from '@store/selectors/site.selectors';
import type { IAdminProfession } from '@apis/adminProfessions';
import { Button } from '@components/ui/Button/Button';
import { Toasts } from '@components/ui/Toasts/Toasts';
import { useToasts } from '@components/ui/Toasts/useToasts';
import { PlusIcon, SearchIcon } from '@/utils/icons';
import { humanizeCamelCase, tradeLabel } from '@utils/tradeLabel';
import { useAdminProfessions, useToggleProfessionActive } from './hooks/useAdminProfessions';
import { ProfessionsTable } from './ProfessionsTable/ProfessionsTable';
import { ProfessionDialog } from './ProfessionDialog/ProfessionDialog';
import { PageHeader } from '@components/ui/PageHeader/PageHeader';
import styles from '../AdminPage.module.css';

function matchesSearch(profession: IAdminProfession, query: string, t: TFunction): boolean {
  const group = profession.tradeGroup
    ? t(`trades.${profession.tradeGroup}`, { defaultValue: humanizeCamelCase(profession.tradeGroup) })
    : '';
  return [
    tradeLabel(t, profession.tradeId),
    group,
    profession.specialty ?? '',
    profession.contractor ?? '',
  ].some((field) => field.toLowerCase().includes(query));
}

export function ProfessionsTab() {
  const { t } = useTranslation();
  const siteId = useSelector(selectSiteId);
  const { toasts, push, dismiss } = useToasts();

  const { data, isLoading } = useAdminProfessions(siteId ?? null);
  const professions = useMemo(() => data?.professions ?? [], [data]);

  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<IAdminProfession | null>(null);

  const toggleMutation = useToggleProfessionActive(siteId ?? null, push);

  const visible = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return professions;
    return professions.filter((p) => matchesSearch(p, query, t));
  }, [professions, search, t]);

  if (!siteId) return null;

  return (
    <div className={styles.page}>
      <div className={styles.pageHeader}>
        <PageHeader
          title={t('adminProfessions.title')}
          subtitle={t('adminProfessions.subtitle')}
        />
        <div className={styles.toolbar}>
          <div className={styles.searchWrap}>
            <SearchIcon size={15} />
            <input
              className={styles.searchInput}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('adminProfessions.searchPlaceholder')}
            />
          </div>
          <span className={styles.count}>
            {t('adminProfessions.count', { count: visible.length })}
          </span>
          <div className={styles.toolbarSpacer} />
          <Button variant="primary" size="sm" onClick={() => setCreateOpen(true)}>
            <PlusIcon size={14} />
            {t('adminProfessions.add')}
          </Button>
        </div>
      </div>

      <div className={styles.body}>
        <ProfessionsTable
          professions={visible}
          emptyMessage={
            isLoading
              ? t('adminProfessions.loading')
              : search.trim()
                ? t('adminProfessions.noMatches')
                : t('adminProfessions.empty')
          }
          togglingId={toggleMutation.isPending ? toggleMutation.variables.profession._id : null}
          onToggleActive={(profession, active) => {
            if (toggleMutation.isPending) return;
            toggleMutation.mutate({ profession, active });
          }}
          onEdit={setEditing}
        />
      </div>

      <ProfessionDialog
        siteId={siteId}
        open={createOpen}
        profession={null}
        onClose={() => setCreateOpen(false)}
      />
      {editing && (
        <ProfessionDialog
          key={editing._id}
          siteId={siteId}
          open
          profession={editing}
          onClose={() => setEditing(null)}
        />
      )}
      <Toasts toasts={toasts} onDismiss={dismiss} />
    </div>
  );
}
